import type {
  ViagemControle,
  ViagemBilling,
} from 'pages/viagens-controle/types';
import type { DemoState, Trip, Invoice } from './model';

export type EstadiaCommand =
  | { type: 'validate'; id: string }
  | { type: 'rejectTrip'; id: string; reason: string; justificationId: number }
  | { type: 'createInvoice'; ids: string[] }
  | { type: 'approveInvoice'; id: string }
  | { type: 'resubmitInvoice'; id: string }
  | { type: 'dispatch'; id: string }
  | {
      type: 'rejectInvoice';
      id: string;
      reason: string;
      justificationId: number;
    };
export type Justification = { id: number; description: string };

export function sourceMinutes(value?: string | null): number {
  if (!value) return 0;
  const match = /^(-?)(\d+):(\d{2})(?::(\d{2}))?$/.exec(value);
  if (!match || Number(match[3]) > 59 || Number(match[4] || 0) > 59) return 0;
  return (
    (match[1] ? -1 : 1) *
    (Number(match[2]) * 60 + Number(match[3]) + Number(match[4] || 0) / 60)
  );
}

export function adaptTrip(row: ViagemControle): Trip {
  const cents = Math.round(Number(row.valorEstadia || 0) * 100);
  const remaining = sourceMinutes(row.limiteRestante);
  const status = row.status?.trim();
  const completed = status
    ? /^(finalizad[oa]|encerrad[oa]|conclu[ií]d[oa])(?:\s|$)/i.test(status)
    : false;
  const late = Boolean(
    row.chegada &&
    row.agendamento &&
    Date.parse(row.chegada) > Date.parse(row.agendamento)
  );
  return {
    id: String(row.id),
    sourceId: row.idViagem || String(row.id),
    sourceExitAt: row.saida || undefined,
    history: (row.activities || []).map((activity) => ({
      at: activity.createdAt,
      actor: activity.user?.name || 'Usuário não informado',
      message: activity.description,
    })),
    plate: row.placa || 'Não informada',
    carrier: row.carrier?.tradeName || 'Transportadora não informada',
    unit: 'Não informada',
    circuit: false,
    site: 'Não informado',
    cnpj: 'Não informado',
    address: 'Não informado',
    category: 'Não informada',
    tons: 0,
    sourceMissing: [
      'Unidade',
      'Circuito',
      'Tipo de local',
      'CNPJ e endereço da NF',
      'Categoria de produto',
      'Tonelagem',
      ...(!row.totalPermanencia ? ['Permanência total'] : []),
      ...(!row.limiteRestante ? ['Horas restantes'] : []),
      ...(row.valorEstadia == null ? ['Valor de estadia'] : []),
    ],
    operation:
      row.fimCarregamento || sourceMinutes(row.permanenciaExp) > 0
        ? 'Descarga e carregamento'
        : 'Descarga',
    events: {
      scheduled: row.agendamento || '',
      arrival: row.chegada || '',
      unloaded: row.fimDescarregamento || undefined,
      loaded: row.fimCarregamento || undefined,
      exit: undefined,
    },
    validation: row.rejectedAt
      ? 'rejected'
      : row.approvedAt
        ? 'validated'
        : 'pending',
    validationReason: row.rejections?.[0]?.comment,
    assessment: {
      eligible: cents > 0 || !late,
      rec: sourceMinutes(row.permanenciaRec),
      exp: sourceMinutes(row.permanenciaExp),
      total: sourceMinutes(row.totalPermanencia),
      remaining,
      excess: sourceMinutes(row.estadiaPagar),
      cents,
      complete: completed,
      phase: row.status || 'Não informado',
      status:
        cents > 0 ? 'pay' : late ? 'none' : remaining <= 120 ? 'risk' : 'ok',
    },
  };
}

export function adaptInvoice(row: ViagemBilling): Invoice {
  return {
    id: String(row.id),
    carrier: row.carrier?.tradeName || 'Transportadora não informada',
    tripIds: (row.billingItems || []).map((item) =>
      String(item.viagemControleId)
    ),
    status: row.reversedAt
      ? 'reversed'
      : row.rejectedAt
        ? 'rejected'
        : row.approvedAt
          ? 'approved'
          : 'pending',
    createdAt: row.createdAt,
    protocol: row.approvalProtocol || undefined,
    reason: row.rejections?.[0]?.comment,
    totalCents:
      row.totalValue == null
        ? undefined
        : Math.round(Number(row.totalValue) * 100),
    history: (row.activities || []).map((activity) => ({
      at: activity.createdAt,
      actor: activity.user?.name || 'Sistema',
      message: activity.description,
    })),
  };
}

export function adaptState(
  trips: ViagemControle[],
  invoices: ViagemBilling[]
): DemoState {
  const deliveries: DemoState['deliveries'] = [];
  for (const invoice of invoices) {
    for (const activity of invoice.activities || []) {
      if (
        activity.operation !== 'EXPORT' ||
        activity.description !== 'Encaminhamento financeiro Deloitte'
      )
        continue;
      try {
        const data = JSON.parse(activity.dataInfo || '{}');
        if (
          data.action !== 'FINANCE_DISPATCH' ||
          data.protocol !== invoice.approvalProtocol ||
          !['sent', 'captured'].includes(data.status)
        )
          continue;
        deliveries.push({
          id: String(activity.id),
          invoiceId: String(invoice.id),
          at: activity.createdAt,
          recipients: data.recipients,
          protocol: data.protocol,
          status:
            data.status === 'sent' ? 'Enviado' : 'Capturado no ambiente local',
        });
      } catch {
        continue;
      }
    }
  }
  return {
    version: 2,
    trips: trips.map(adaptTrip),
    invoices: invoices.map(adaptInvoice),
    deliveries,
  };
}
